import { STRINGS } from '@/strings/strings';
import type { PreviewEnvelope } from '../../types';
import { ReviewScreen } from '../review/ReviewScreen';

/**
 * Sebastian ruling 2026-09-24 — read-only book viewer.
 *
 * Fires from "Meine Bücher" on any post-approval status
 * (`print_approved`, `prepress`, `handoff_pod`, `shipped`,
 * `delivered`). Reuses `ReviewScreen` in `readOnly` mode so the
 * customer sees the exact same page renderer he approved for print,
 * with every mutation control hidden. Above the pages sits a small
 * header (title + subtitle + status line) that this component owns.
 *
 * ═══ NO MOUNT-REFETCH (Sebastian ruling 2026-09-24, second pass) ══
 *
 * Pre-2026-09-24 this component fired `useEffect(() => onRefetch(),
 * [])` to "guard against stale 1-hour signed URLs". The rationale
 * was wrong: `usePreviewEnvelope` state resets on every mount (no
 * persisted cache), so `BookShell`'s fetch that landed a moment
 * ago is by definition < 1 second old. The mount-refetch doubled
 * every tap-to-open envelope round-trip and — worse — on refetch
 * failure (`use-preview-envelope.ts:167-171` sets `{data: null,
 * error, loading: false}` on catch) it WIPED the envelope and
 * bounced BookRoute back to the placeholder. That was part of
 * iPhone-observed "6-30 seconds blank" on cold-load. Removed.
 *
 * A customer who left the tab open past the 1-hour TTL and returned
 * would still see 403 on the `<img>` — that is a real but separate
 * concern; the tap-to-open path from Meine Bücher / email link
 * never hits that window (the shell fetch is always fresh). If it
 * ever needs handling, an image-error boundary that requests
 * refetch is the correct fix, not a blanket mount-refetch.
 *
 * ═══ RETENTION FALLBACK (Datenschutzerklärung §3.5) ══════════════
 *
 * Section 3.5 of the privacy policy promises deletion of character
 * images, book pages, and the print file 24 months after delivery
 * so the customer can reorder up to that point. Today the backend
 * never deletes; this fallback is defensive so once the retention
 * job lands (tracker `[book-assets-24m-deletion]`), the view
 * degrades cleanly to a single explanatory line instead of showing
 * broken image icons. Detection heuristic:
 * `cover.imageUrl === null && every page composedImageUrl === null`.
 * The post-approval invariant is that every slot was `ready` when
 * the customer released the book, so a null across the board
 * cannot mean "still rendering" — it can only mean "deleted".
 */
export interface PostOrderViewProps {
  readonly envelope: PreviewEnvelope;
  readonly onRefetch: () => void;
}

type PostOrderStatus = 'print_approved' | 'prepress' | 'handoff_pod' | 'shipped' | 'delivered';

const POST_ORDER_STATUSES: readonly PostOrderStatus[] = [
  'print_approved',
  'prepress',
  'handoff_pod',
  'shipped',
  'delivered',
];

export function isPostOrderStatus(status: string): status is PostOrderStatus {
  return (POST_ORDER_STATUSES as readonly string[]).includes(status);
}

function derivePostOrderStatusLabel(status: PostOrderStatus): string {
  switch (status) {
    case 'print_approved':
    case 'prepress':
    case 'handoff_pod':
      return STRINGS.book.postOrder.status.inPrint;
    case 'shipped':
      return STRINGS.book.postOrder.status.shipped;
    case 'delivered':
      return STRINGS.book.postOrder.status.delivered;
  }
}

export function PostOrderView({ envelope, onRefetch }: PostOrderViewProps) {
  // Runtime narrowing — BookRoute only routes this component the five
  // post-approval statuses, but the envelope carries the wider union.
  // The invariant is checked at the type-guard boundary rather than
  // asserted so a wiring drift surfaces at render time instead of
  // crashing on `derivePostOrderStatusLabel`.
  if (!isPostOrderStatus(envelope.status)) return null;
  const statusLabel = derivePostOrderStatusLabel(envelope.status);

  const assetsDeleted =
    envelope.cover.imageUrl === null && envelope.pages.every((p) => p.composedImageUrl === null);

  return (
    <>
      <div
        className="mx-auto flex w-full max-w-[680px] flex-col gap-3 px-4 pt-2"
        data-testid="post-order-view"
      >
        <h1 className="text-2xl font-semibold text-dream-navy">{STRINGS.book.postOrder.title}</h1>
        {envelope.title.length > 0 && (
          <p className="text-lg font-semibold text-dream-navy">„{envelope.title}"</p>
        )}
        <p className="text-base text-dream-navy">{STRINGS.book.postOrder.subtitle}</p>
        <p
          role="status"
          className="self-start rounded-full bg-dream-navy/10 px-3 py-1 text-sm font-semibold text-dream-navy"
        >
          {statusLabel}
        </p>
      </div>
      {assetsDeleted ? (
        <div className="mx-auto mt-6 max-w-[680px] px-4 pb-6">
          <p className="text-center text-sm text-dream-navy">
            {STRINGS.book.postOrder.assets.deletedAfterRetention}
          </p>
        </div>
      ) : (
        <ReviewScreen envelope={envelope} onRefetch={onRefetch} readOnly />
      )}
    </>
  );
}
